/* Shared lock/unlock/amend/versions/view/download controls for signed clinical records.
   Usage: <ClinicalRecordLockControls kind="progress_note" record={note} onChange={reload} />
   Supported kinds: progress_note, assessment, treatment_plan, crisis_plan, consent_form, and chart_note. */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import dayjs from "dayjs";
import { api, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { SlidePanel } from "./SlidePanel";
import { Btn } from "./UI";
import { FLabel } from "../pages/engagement/FormControls";
import { Lock, Unlock, History, ShieldAlert, FileText, Download, Timer, HelpCircle } from "lucide-react";

const KIND_LABELS = {
  progress_note: "Progress Note",
  assessment: "Assessment",
  treatment_plan: "Treatment Plan",
  crisis_plan: "Crisis or Safety Plan",
  consent_form: "Consent Form",
  chart_note: "Medical Note",
};

const DEFAULT_PATH_BUILDER = (kind, rec_id) => `/clinical-records/${kind}/${rec_id}`;
const RENDER_PATH = (kind, id) => `/clinical-records/${kind}/${id}/render`;

function useCountdown(untilIso) {
  const [text, setText] = useState("");
  useEffect(() => {
    if (!untilIso) return;
    const tick = () => {
      const ms = dayjs(untilIso).diff(dayjs());
      if (ms <= 0) { setText("expired"); return; }
      const mins = Math.floor(ms / 60000);
      const h = Math.floor(mins / 60);
      const m = mins % 60;
      setText(h > 0 ? `${h}h ${m}m` : `${m}m`);
    };
    tick();
    const t = setInterval(tick, 30000);
    return () => clearInterval(t);
  }, [untilIso]);
  return text;
}

export default function ClinicalRecordLockControls({
  kind, record, onChange, amendableFields = [], endpointPathBuilder, testKind,
}) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const isLocked = !!record?.locked;
  const versionCount = record?.version_count || 1;
  const [unlockPanel, setUnlockPanel] = useState(false);
  const [unlockReason, setUnlockReason] = useState("");
  const [unlockWindow, setUnlockWindow] = useState(2);
  const [requestPanel, setRequestPanel] = useState(false);
  const [requestReason, setRequestReason] = useState("");
  const [amendPanel, setAmendPanel] = useState(false);
  const [amendSummary, setAmendSummary] = useState("");
  const [amendForm, setAmendForm] = useState({});
  const [versionsPanel, setVersionsPanel] = useState(null);
  const label = KIND_LABELS[kind] || "Record";
  const tk = testKind || kind;
  const basePath = (id) => (endpointPathBuilder ? endpointPathBuilder(id) : DEFAULT_PATH_BUILDER(kind, id));
  const countdown = useCountdown(record?.unlocked_until);
  const inWindow = !isLocked && !!record?.unlocked_until && countdown !== "expired";

  const hasBeenSigned = !!(record?.signature || record?.signed || record?.signed_at ||
                           record?.status === "signed" || record?.status === "completed");

  const openAmend = () => {
    const initial = {};
    amendableFields.forEach((f) => { initial[f.key] = record?.[f.key] ?? ""; });
    setAmendForm(initial);
    setAmendSummary("");
    setAmendPanel(true);
  };

  const submitUnlock = async (e) => {
    e.preventDefault();
    if (!unlockReason.trim()) return toast.error("Unlock reason is required");
    try {
      await api.post(`${basePath(record.id)}/unlock`, {
        reason: unlockReason.trim(),
        window_hours: unlockWindow,
      });
      const wtxt = unlockWindow > 0 ? ` Amendment window: ${unlockWindow}h.` : "";
      toast.success(`${label} unlocked.${wtxt} Amendments will preserve the original in the audit trail.`);
      setUnlockPanel(false); setUnlockReason(""); setUnlockWindow(2);
      onChange?.();
    } catch (err) { toast.error(apiError(err)); }
  };

  const submitUnlockRequest = async (e) => {
    e.preventDefault();
    if (requestReason.trim().length < 10) {
      return toast.error("Please provide at least 10 characters explaining why the record needs to be unlocked");
    }
    try {
      await api.post(`${basePath(record.id)}/unlock-request`, { reason: requestReason.trim() });
      toast.success("Unlock request sent to admin. You will get an alert when it is reviewed.");
      setRequestPanel(false); setRequestReason("");
      onChange?.();
    } catch (err) { toast.error(apiError(err)); }
  };

  const submitAmend = async (e) => {
    e.preventDefault();
    if (amendSummary.trim().length < 5) return toast.error("Change summary must be at least 5 characters");
    try {
      const payload = { ...amendForm, change_summary: amendSummary.trim() };
      await api.post(`${basePath(record.id)}/amend`, payload);
      toast.success(`${label} amendment saved. Original version retained.`);
      setAmendPanel(false); setAmendSummary("");
      onChange?.();
    } catch (err) { toast.error(apiError(err)); }
  };

  const openVersions = async () => {
    try {
      const r = await api.get(`${basePath(record.id)}/versions`);
      setVersionsPanel(r.data);
    } catch (err) { toast.error(apiError(err)); }
  };

  const openRender = async (download, versionId) => {
    // Prefer generic render endpoint. For custom-path callers, still use the generic /clinical-records path
    // since the render is centralized. `versionId` optionally targets a specific version snapshot.
    try {
      const params = {};
      if (download) {
        params.download = 1;
        // HIPAA requires a business reason for every PHI download. Simple prompt with the
        // three common categories; user may append free text.
        const reason = window.prompt(
          "Business reason for downloading PHI (required):\n\n" +
          "Type one of: continuity of care, client request, legal\n" +
          "or a short free-text explanation.",
          "continuity of care"
        );
        if (!reason || !reason.trim()) {
          toast.error("Download cancelled. A business reason is required.");
          return;
        }
        params.business_reason = reason.trim();
      }
      if (versionId) params.version = versionId;
      const r = await api.get(RENDER_PATH(kind, record.id), { params, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "text/html" }));
      const w = window.open(url, "_blank", "noopener,noreferrer");
      if (!w) toast.error("Pop-up blocked. Allow pop-ups for this site to view the document.");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div className="inline-flex flex-wrap items-center gap-2" data-testid={`clinical-lock-controls-${tk}`}>
      {isLocked && (
        <span data-testid={`clinical-lock-badge-${tk}`}
          className="inline-flex items-center gap-1 rounded-full bg-[#EDE7DC] px-2 py-0.5 text-[11px] font-bold text-[#4E342E]">
          <Lock size={11} /> Locked
        </span>
      )}
      {inWindow && (
        <span data-testid={`clinical-unlock-countdown-${tk}`}
          title={`Auto re-locks at ${dayjs(record.unlocked_until).format("MMM D, h:mm A")}`}
          className="inline-flex items-center gap-1 rounded-full bg-[#FFF3E0] px-2 py-0.5 text-[11px] font-bold text-[#8D5A00]">
          <Timer size={11} /> Editable {countdown}
        </span>
      )}
      {versionCount > 1 && (
        <span data-testid={`clinical-version-badge-${tk}`}
          className="inline-flex items-center gap-1 rounded-full bg-[#FFF3E0] px-2 py-0.5 text-[11px] font-bold text-[#8D5A00]">
          v{versionCount}
        </span>
      )}
      {hasBeenSigned && (
        <Btn variant="outline" data-testid={`clinical-view-btn-${tk}`}
          onClick={() => openRender(false)} title="View printable document with signature audit">
          <FileText size={12} /> View
        </Btn>
      )}
      {hasBeenSigned && (
        <Btn variant="outline" data-testid={`clinical-download-btn-${tk}`}
          onClick={() => openRender(true)} title="Open printable document, then use browser print / Save as PDF">
          <Download size={12} /> Download
        </Btn>
      )}
      {(versionCount > 1 || isLocked || hasBeenSigned) && (
        <Btn variant="outline" data-testid={`clinical-history-btn-${tk}`} onClick={openVersions}>
          <History size={12} /> History
        </Btn>
      )}
      {isAdmin && isLocked && (
        <Btn variant="outline" data-testid={`clinical-unlock-btn-${tk}`}
          onClick={() => setUnlockPanel(true)} className="border-[#B26A00] text-[#8D5A00]">
          <Unlock size={12} /> Unlock
        </Btn>
      )}
      {!isAdmin && isLocked && (
        <Btn variant="outline" data-testid={`clinical-unlock-request-btn-${tk}`}
          onClick={() => setRequestPanel(true)} className="border-[#B26A00] text-[#8D5A00]">
          <HelpCircle size={12} /> Request Unlock
        </Btn>
      )}
      {!isLocked && amendableFields.length > 0 && (versionCount > 1 || hasBeenSigned) && (
        <Btn variant="outline" data-testid={`clinical-amend-btn-${tk}`}
          onClick={openAmend} className="border-[#B26A00] text-[#8D5A00]">
          <ShieldAlert size={12} /> Amend
        </Btn>
      )}

      <SlidePanel open={unlockPanel} onClose={() => setUnlockPanel(false)}
        title={`Unlock ${label}`}
        subtitle="Admin action. Amendments preserve the original snapshot in the audit trail."
        testId={`clinical-unlock-panel-${tk}`}>
        <form onSubmit={submitUnlock} className="space-y-3">
          <div>
            <FLabel>Unlock reason (required)</FLabel>
            <textarea data-testid={`clinical-unlock-reason-${tk}`} rows={3} required
              value={unlockReason} onChange={(e) => setUnlockReason(e.target.value)}
              placeholder="Documentation error, corrected diagnosis, missed detail, etc."
              className="w-full rounded-lg border border-[#E5DFD3] bg-white px-3 py-2 text-sm outline-none focus:border-[#8D6E63]" />
          </div>
          <div>
            <FLabel>Amendment window (hours) - the record auto re-locks after this window</FLabel>
            <select data-testid={`clinical-unlock-window-${tk}`} value={unlockWindow}
              onChange={(e) => setUnlockWindow(parseInt(e.target.value, 10))}
              className="w-full rounded-lg border border-[#E5DFD3] bg-white px-3 py-2 text-sm outline-none focus:border-[#8D6E63]">
              <option value={1}>1 hour</option>
              <option value={2}>2 hours (recommended)</option>
              <option value={4}>4 hours</option>
              <option value={8}>8 hours</option>
              <option value={24}>24 hours</option>
              <option value={0}>No window (until next re-sign)</option>
            </select>
          </div>
          <div className="flex justify-end gap-2 border-t border-[#F1EEE7] pt-3">
            <Btn type="button" variant="ghost" onClick={() => setUnlockPanel(false)}>Cancel</Btn>
            <Btn data-testid={`clinical-unlock-confirm-${tk}`} type="submit">
              <Unlock size={12} /> Unlock
            </Btn>
          </div>
        </form>
      </SlidePanel>

      <SlidePanel open={requestPanel} onClose={() => setRequestPanel(false)}
        title={`Request Unlock: ${label}`}
        subtitle="Only admin can unlock. Your reason is logged for compliance and sent to admins as an alert."
        testId={`clinical-request-panel-${tk}`}>
        <form onSubmit={submitUnlockRequest} className="space-y-3">
          <div className="rounded-xl border border-[#FFCC80] bg-[#FFF3E0] p-3 text-xs text-[#8D5A00]">
            You are asking an admin to unlock this signed record so you can make an amendment. The admin will grant a 1-24 hour window during which you can edit. Everything is time-logged.
          </div>
          <div>
            <FLabel>Reason (minimum 10 characters, required)</FLabel>
            <textarea data-testid={`clinical-request-reason-${tk}`} rows={4} required minLength={10}
              value={requestReason} onChange={(e) => setRequestReason(e.target.value)}
              placeholder="What field needs to change and why? Include date, missed detail, or corrected value."
              className="w-full rounded-lg border border-[#E5DFD3] bg-white px-3 py-2 text-sm outline-none focus:border-[#8D6E63]" />
          </div>
          <div className="flex justify-end gap-2 border-t border-[#F1EEE7] pt-3">
            <Btn type="button" variant="ghost" onClick={() => setRequestPanel(false)}>Cancel</Btn>
            <Btn data-testid={`clinical-request-submit-${tk}`} type="submit">
              <HelpCircle size={12} /> Submit Request
            </Btn>
          </div>
        </form>
      </SlidePanel>

      <SlidePanel open={amendPanel} onClose={() => setAmendPanel(false)}
        title={`Amend ${label}`}
        subtitle="The current version is snapshotted into audit history. This amendment becomes the new current version and re-locks the record."
        testId={`clinical-amend-panel-${tk}`}>
        <form onSubmit={submitAmend} className="space-y-3">
          {amendableFields.map((f) => (
            <div key={f.key}>
              <FLabel>{f.label}</FLabel>
              {f.type === "textarea" ? (
                <textarea rows={f.rows || 3} value={amendForm[f.key] || ""}
                  onChange={(e) => setAmendForm((s) => ({ ...s, [f.key]: e.target.value }))}
                  data-testid={`clinical-amend-field-${tk}-${f.key}`}
                  className="w-full rounded-lg border border-[#E5DFD3] bg-white px-3 py-2 text-sm outline-none focus:border-[#8D6E63]" />
              ) : (
                <input type={f.type || "text"} value={amendForm[f.key] || ""}
                  onChange={(e) => setAmendForm((s) => ({ ...s, [f.key]: e.target.value }))}
                  data-testid={`clinical-amend-field-${tk}-${f.key}`}
                  className="w-full rounded-lg border border-[#E5DFD3] bg-white px-3 py-2 text-sm outline-none focus:border-[#8D6E63]" />
              )}
            </div>
          ))}
          <div>
            <FLabel>Change summary (min 5 chars) *</FLabel>
            <textarea data-testid={`clinical-amend-summary-${tk}`} rows={2} required
              value={amendSummary} onChange={(e) => setAmendSummary(e.target.value)}
              placeholder="Which field(s) are changing, and why?"
              className="w-full rounded-lg border border-[#E5DFD3] bg-white px-3 py-2 text-sm outline-none focus:border-[#8D6E63]" />
          </div>
          <div className="flex justify-end gap-2 border-t border-[#F1EEE7] pt-3">
            <Btn type="button" variant="ghost" onClick={() => setAmendPanel(false)}>Cancel</Btn>
            <Btn data-testid={`clinical-amend-save-${tk}`} type="submit">Save Amendment</Btn>
          </div>
        </form>
      </SlidePanel>

      <SlidePanel open={!!versionsPanel} onClose={() => setVersionsPanel(null)}
        title={`${label} - Version History`}
        subtitle={versionsPanel ? `${versionsPanel.version_count} version${versionsPanel.version_count === 1 ? "" : "s"} retained. Latest at the top.` : ""}
        testId={`clinical-versions-panel-${tk}`}>
        {versionsPanel && (
          <div className="space-y-3">
            <div className="rounded-xl border border-[#4E342E] bg-[#FDFCF8] p-3">
              <div className="flex items-center gap-2">
                <span className="rounded-full bg-[#4E342E] px-2 py-0.5 text-[10px] font-bold text-white">CURRENT</span>
                <span className="text-xs font-bold text-[#3E2723]">v{versionsPanel.version_count}</span>
                {versionsPanel.current.locked && <span className="inline-flex items-center gap-0.5 text-[10px] text-[#4E342E]"><Lock size={10} /> Locked</span>}
                <Btn variant="outline" className="ml-auto"
                  data-testid={`clinical-version-view-current-${tk}`}
                  onClick={() => openRender(false)}>
                  <FileText size={10} /> View
                </Btn>
              </div>
              {versionsPanel.current.amended_by_name && (
                <div className="mt-2 text-xs text-[#8D5A00]">Amended by {versionsPanel.current.amended_by_name} on {dayjs(versionsPanel.current.amended_at).format("MMM D, YYYY h:mm A")}: {versionsPanel.current.last_change_summary}</div>
              )}
              {versionsPanel.current.signed_at && !versionsPanel.current.amended_at && (
                <div className="mt-2 text-xs text-[#7B6157]">Originally signed on {dayjs(versionsPanel.current.signed_at).format("MMM D, YYYY h:mm A")}</div>
              )}
            </div>
            {versionsPanel.versions.map((v) => (
              <div key={v.id} className="rounded-xl border border-[#EFEBE0] bg-white p-3"
                data-testid={`clinical-version-${tk}-${v.version_number}`}>
                <div className="flex items-center gap-2">
                  <span className="rounded-full bg-[#EFEBE0] px-2 py-0.5 text-[10px] font-bold text-[#6D4C41]">v{v.version_number}</span>
                  <span className="text-[11px] text-[#7B6157]">{dayjs(v.created_at).format("MMM D, YYYY h:mm A")}</span>
                </div>
                <div className="mt-1 text-xs text-[#7B6157]"><strong>Edited by:</strong> {v.editor_name} ({v.editor_role})</div>
                <div className="text-xs text-[#5D4037]"><strong>Change summary:</strong> {v.change_summary}</div>
                {amendableFields.map((f) => (
                  v.snapshot?.[f.key] ? (
                    <div key={f.key} className="mt-1 text-xs">
                      <span className="text-[#7B6157]">{f.label}: </span>
                      <span className="text-[#3E2723]">{typeof v.snapshot[f.key] === "string" ? v.snapshot[f.key] : JSON.stringify(v.snapshot[f.key]).slice(0, 200)}</span>
                    </div>
                  ) : null
                ))}
              </div>
            ))}
            {versionsPanel.versions.length === 0 && (
              <div className="text-xs italic text-[#7B6157]">No prior versions. Amendments will appear here.</div>
            )}
          </div>
        )}
      </SlidePanel>
    </div>
  );
}
